/* ---------------------------------------------------------------------------
   Testimonial marquee
   Each column drifts continuously and can also be dragged. Both share one
   position value, so a drag just moves the same float rather than switching
   to a different mechanism.

   Transform based rather than native scroll on purpose: a looping column
   never reaches an end, so a native scroller would swallow the wheel and
   trap the page. Here the wheel is left alone and the page scrolls normally.

   Touch: press and hold a card and the column stops so it can be read.
   Lifting the finger resumes after the same delay a drag uses. The listeners
   are all passive and never preventDefault, so a swipe still scrolls the
   page: when the browser takes the gesture over it fires pointercancel,
   which releases the hold.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var columns = [];
  var running = false;
  var lastTs = 0;

  function Column(el) {
    this.el = el;
    this.track = el.querySelector('[data-tm-track]');
    this.set = el.querySelector('[data-tm-set]');
    if (!this.track || !this.set) return;

    this.dir = el.getAttribute('data-direction') === 'down' ? -1 : 1;
    this.speed = parseFloat(el.getAttribute('data-speed')) || 30;
    this.resumeDelay = parseInt(el.getAttribute('data-resume'), 10);
    if (isNaN(this.resumeDelay)) this.resumeDelay = 900;

    this.pos = 0;
    this.half = 0;
    this.pausedUntil = 0;
    this.dragging = false;
    this.held = false;
    this.velocity = 0;
    this.visible = true;

    this.measure();
    if (this.dir < 0) this.pos = this.half;
    this.apply();

    if (el.getAttribute('data-drag') === 'true') this.bindDrag();
    this.bindHold();
    this.watch();
  }

  Column.prototype.measure = function () {
    var h = this.set.offsetHeight;
    /* zero means this column is hidden at the current breakpoint. Without
       this guard half would come out as just the gap and the column would
       churn through pointless wraps while display:none. */
    if (!h) { this.half = 0; return; }
    var cs = window.getComputedStyle(this.track);
    var gap = parseFloat(cs.rowGap || cs.gap) || 0;
    this.half = h + gap;
  };

  Column.prototype.wrap = function () {
    if (!this.half) return;
    while (this.pos >= this.half) this.pos -= this.half;
    while (this.pos < 0) this.pos += this.half;
  };

  Column.prototype.apply = function () {
    this.track.style.transform = 'translate3d(0,' + (-this.pos).toFixed(2) + 'px,0)';
  };

  Column.prototype.tick = function (dt, now) {
    if (!this.half || !this.visible) return;

    if (this.dragging) return;

    /* a finger is resting on the column: freeze it dead, including any
       leftover flick momentum, so the card can actually be read */
    if (this.held) return;

    if (this.velocity) {
      this.pos += this.velocity * dt;
      this.velocity *= Math.pow(0.002, dt);
      if (Math.abs(this.velocity) < 8) this.velocity = 0;
      this.wrap();
      this.apply();
      return;
    }

    if (reduced || now < this.pausedUntil) return;

    this.pos += this.dir * this.speed * dt;
    this.wrap();
    this.apply();
  };

  /* ---- press and hold to read -------------------------------------------
     Touch and pen only. A mouse is left alone: on desktop the column is
     draggable instead, and pausing under the cursor was not asked for.

     Every listener here is passive and never calls preventDefault, so the
     page keeps scrolling normally. A stationary press fires no scroll, so
     no pointercancel arrives and the column stays frozen for as long as the
     finger is down. Start scrolling and the browser claims the gesture,
     fires pointercancel, and the column picks up again.
     ---------------------------------------------------------------------- */
  Column.prototype.bindHold = function () {
    var self = this;

    function isTouch(e) {
      /* older engines report an empty pointerType; treat those as touch
         only when the device has no fine pointer */
      if (e.pointerType === 'mouse') return false;
      if (e.pointerType) return true;
      return !fine;
    }

    function hold(e) {
      if (!isTouch(e)) return;
      if (self.dragging) return;
      self.held = true;
      self.velocity = 0;
      self.el.classList.add('is-held');
    }

    function release() {
      if (!self.held) return;
      self.held = false;
      self.el.classList.remove('is-held');
      /* same settling delay a drag uses, so it does not lurch back into
         motion the instant the finger lifts */
      self.pausedUntil = performance.now() + self.resumeDelay;
    }

    if (window.PointerEvent) {
      this.el.addEventListener('pointerdown', hold, { passive: true });
      this.el.addEventListener('pointerup', release, { passive: true });
      this.el.addEventListener('pointercancel', release, { passive: true });
      this.el.addEventListener('pointerleave', release, { passive: true });
    } else {
      this.el.addEventListener('touchstart', hold, { passive: true });
      this.el.addEventListener('touchend', release, { passive: true });
      this.el.addEventListener('touchcancel', release, { passive: true });
    }

    /* a finger can be lifted outside the column, or the tab can go away
       mid-press, and neither always delivers pointerup to the element */
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) release();
    });
  };

  Column.prototype.bindDrag = function () {
    var self = this;
    var startY = 0;
    var startPos = 0;
    var lastY = 0;
    var lastMove = 0;
    var id = null;

    this.el.addEventListener('pointerdown', function (e) {
      /* touch is left to the page so vertical swipes still scroll it */
      if (e.pointerType !== 'mouse' && !fine) return;
      id = e.pointerId;
      self.dragging = true;
      self.held = false;
      self.velocity = 0;
      startY = lastY = e.clientY;
      startPos = self.pos;
      lastMove = performance.now();
      self.el.setPointerCapture(id);
      self.el.classList.add('is-dragging');
    });

    this.el.addEventListener('pointermove', function (e) {
      if (!self.dragging || e.pointerId !== id) return;
      e.preventDefault();
      self.pos = startPos - (e.clientY - startY);
      self.wrap();
      self.apply();

      var now = performance.now();
      var dt = (now - lastMove) / 1000;
      if (dt > 0) self.velocity = -(e.clientY - lastY) / dt;
      lastY = e.clientY;
      lastMove = now;
    });

    function release(e) {
      if (!self.dragging || (e && e.pointerId !== id)) return;
      self.dragging = false;
      self.el.classList.remove('is-dragging');
      if (id !== null && self.el.hasPointerCapture && self.el.hasPointerCapture(id)) {
        self.el.releasePointerCapture(id);
      }
      /* cap the flick so it cannot fly off */
      self.velocity = Math.max(-1800, Math.min(1800, self.velocity));
      self.pausedUntil = performance.now() + self.resumeDelay;
      id = null;
    }

    this.el.addEventListener('pointerup', release);
    this.el.addEventListener('pointercancel', release);
    this.el.addEventListener('lostpointercapture', release);
  };

  /* stop working entirely when the section is off screen */
  Column.prototype.watch = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      self.visible = entries[0].isIntersecting;
    }, { rootMargin: '200px 0px' });
    io.observe(this.el);
  };

  function loop(ts) {
    if (!lastTs) lastTs = ts;
    var dt = Math.min((ts - lastTs) / 1000, 0.05); /* clamp after a tab switch */
    lastTs = ts;
    for (var i = 0; i < columns.length; i++) columns[i].tick(dt, ts);
    window.requestAnimationFrame(loop);
  }

  function start() {
    if (running || !columns.length) return;
    running = true;
    window.requestAnimationFrame(loop);
  }

  function init(scope) {
    var root = scope || document;
    [].slice.call(root.querySelectorAll('[data-tm-col]')).forEach(function (el) {
      if (el.hpTm) return;
      el.hpTm = true;
      var col = new Column(el);
      if (col.track) columns.push(col);
    });
    start();
  }

  function remeasure() {
    for (var i = 0; i < columns.length; i++) {
      columns[i].measure();
      columns[i].wrap();
      columns[i].apply();
    }
  }

  var rt = null;
  window.addEventListener('resize', function () {
    if (rt) window.clearTimeout(rt);
    rt = window.setTimeout(remeasure, 150);
  });

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
  window.addEventListener('load', remeasure);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
